import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Heart, MessageCircle, Share2, ArrowLeft } from 'lucide-react';
import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark.css';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import AttestBadge from '../AttestBadge';
import { formatReadTime } from '../../utils';
import { supabase } from '../../supabaseClient';
import { useTheme } from '../../ThemeContext';


export default function ReaderView({ 
  currentPost,
  setCurrentPost,
  currentUser,
  walletAddress,
  subscribedAuthors,
  editPost,
  deletePost,
  subscribeToAuthor,
  unsubscribeFromAuthor,
  setView,
  setSelectedAuthor,
  onShowCommentModal,
  onShowShareModal
}) {
  const { t, isDark } = useTheme();
  const [hasLiked, setHasLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [previousView, setPreviousView] = useState('home');
  const [isFollowing, setIsFollowing] = useState(false);

  // Track where we came from
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from');
    if (from === 'author') {
      setPreviousView('authorProfile');
    } else {
      setPreviousView('home');
    }
  }, []);

  // Check if user is following this author
  useEffect(() => {
    const checkFollowStatus = async () => {
      if (!walletAddress || !currentPost?.author_id) {
        setIsFollowing(false);
        return;
      }
      
      try {
        // Get user's authors
        const { data: userAuthors, error: authorsError } = await supabase
          .from('authors')
          .select('id')
          .eq('wallet_address', walletAddress);
        
        if (authorsError || !userAuthors || userAuthors.length === 0) {
          setIsFollowing(false);
          return;
        }
        
        // Check if any of user's authors follow this post's author
        const { data: followData, error: followError } = await supabase
          .from('follows')
          .select('id')
          .eq('follower_entity_type', 'individual')
          .in('follower_entity_id', userAuthors.map(a => a.id))
          .eq('following_entity_type', 'individual')
          .eq('following_entity_id', currentPost.author_id)
          .limit(1);
        
        if (!followError) {
          setIsFollowing(followData && followData.length > 0);
        }
      } catch (error) {
        console.error('Error checking follow status:', error);
        setIsFollowing(false);
      }
    };
    
    checkFollowStatus();
  }, [walletAddress, currentPost?.author_id]);

  useEffect(() => {
    setTimeout(() => {
      const blocks = document.querySelectorAll('.limestone-prose pre code');
      console.log('[MATH] blocks found:', blocks.length);
      blocks.forEach(block => {
        hljs.highlightElement(block);
      });
      blocks.forEach(block => {
        const text = block.textContent || '';
        const isMath = /[φψσηπ∑∏∈≤≥∞→←⁰¹²³⁴⁵⁶⁷⁸⁹]/.test(text) ||
                       /^\s*(seed|σ|φ|f\s*=|y\s*<|is_leader)/.test(text);
        if (isMath) {
          console.log('[MATH] math block detected:', text.slice(0, 50));
          block.style.fontFamily = "'Georgia', 'Times New Roman', serif";
          block.style.fontSize = '15px';
          block.style.lineHeight = '2';
          block.style.color = '#e8d5b7';
          block.style.background = 'transparent';
          block.innerHTML = block.textContent;
        }
      });

      // Render inline <code> containing math unicode as KaTeX
      const mathChars = /[φψσηπ∑∏∈≤≥≠∞→←⁰¹²³⁴⁵⁶⁷⁸⁹]/;
      const inlineCodes = document.querySelectorAll('.limestone-prose code:not(pre code)');
      inlineCodes.forEach(code => {
        const text = code.textContent || '';
        if (mathChars.test(text)) {
          try {
            katex.render(text, code, { throwOnError: false, displayMode: false });
            code.classList.add('math-inline');
          } catch (e) {
            // KaTeX parse failed — leave as styled code
          }
        }
      });
    }, 0);
  }, [currentPost?.content]);

  const getBrowserFingerprint = () => {
    let fingerprint = localStorage.getItem('user_fingerprint');
    if (!fingerprint) {
      fingerprint = `${navigator.userAgent}_${Date.now()}_${Math.random()}`;
      localStorage.setItem('user_fingerprint', fingerprint);
    }
    return fingerprint;
  };

  useEffect(() => {
    const checkLikeStatus = async () => {
      if (!currentPost?.id) return;
      
      const fingerprint = getBrowserFingerprint();
      const { data } = await supabase
        .from('post_likes')
        .select('id')
        .eq('post_id', currentPost.id)
        .eq('user_fingerprint', fingerprint)
        .maybeSingle();

      setHasLiked(!!data);
    };

    checkLikeStatus();
  }, [currentPost?.id]);

  const handleLike = async () => {
    if (!currentPost?.id || likeLoading) return;
    
    setLikeLoading(true);
    const fingerprint = getBrowserFingerprint();

    try {
      if (hasLiked) {
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', currentPost.id)
          .eq('user_fingerprint', fingerprint);

        if (error) throw error;

        setCurrentPost({
          ...currentPost,
          like_count: Math.max(0, (currentPost.like_count || 0) - 1)
        });
        setHasLiked(false);
      } else {
        const { error } = await supabase
          .from('post_likes')
          .insert({
            post_id: currentPost.id,
            user_fingerprint: fingerprint,
            user_id: currentUser?.id || null
          });

        if (error) throw error;

        setCurrentPost({
          ...currentPost,
          like_count: (currentPost.like_count || 0) + 1
        });
        setHasLiked(true);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      alert('Failed to update like. Please try again.');
    } finally {
      setLikeLoading(false);
    }
  };

  const handleAuthorClick = async () => {
    if (!currentPost.author_id) return;
    
    try {
      // Load the author data
      const { data: authorData, error } = await supabase
        .from('authors')
        .select('*')
        .eq('id', currentPost.author_id)
        .single();
      
      if (error) throw error;
      
      if (authorData && setSelectedAuthor) {
        setSelectedAuthor(authorData);
        // Update URL to indicate we came from a post
        window.history.pushState({}, '', `?from=post&post=${currentPost.id}`);
        setView('authorProfile');
      }
    } catch (error) {
      console.error('Error loading author:', error);
    }
  };

  const handleBackClick = () => {
    // Always go back to home when leaving a post
    setView('home');
    window.history.pushState({}, '', '/');
  };

  if (!currentPost) return null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 animate-in">
      <button
        onClick={handleBackClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: t.text,
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontFamily: 'Poppins, sans-serif',
          marginBottom: '24px',
          padding: '8px 0'
        }}
        onMouseEnter={(e) => e.target.style.opacity = '0.7'}
        onMouseLeave={(e) => e.target.style.opacity = '1'}
      >
        <ArrowLeft size={16} />
        <span>Back to home</span>
      </button>

      <span className="mono text-xs uppercase tracking-wider" style={{ color: t.text }}>
        {currentPost.category}
      </span>
      
      <h1 className="serif-display text-5xl md:text-6xl my-6 leading-tight">
        {currentPost.title}
      </h1>

      {currentPost.subtitle && (
        <p className="text-2xl text-stone-600 mb-8">{currentPost.subtitle}</p>
      )}

      <div className="flex items-center justify-between mb-8 pb-8 border-b border-stone-200">
        <div>
          <div 
            className="flex items-center gap-4"
            onClick={handleAuthorClick}
            style={{
              cursor: currentPost.author_id ? 'pointer' : 'default',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => {
              if (currentPost.author_id) e.currentTarget.style.opacity = '0.7';
            }}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-amber-50 font-semibold" style={{ backgroundColor: t.buttonBg }}>
              {currentPost.author[0].toUpperCase()}
            </div>
            <div>
              <p className="font-semibold">{currentPost.author}</p>
              <p className="text-sm text-stone-500">
                {new Date(currentPost.timestamp).toLocaleDateString()} · {formatReadTime(currentPost.read_time)}
              </p>
            </div>
          </div>
          {currentPost.attest_tx_hash && (
            <div style={{ marginTop: '6px', marginLeft: '64px' }}>
              <AttestBadge
                txHash={currentPost.attest_tx_hash}
                timestamp={currentPost.attest_timestamp}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          {currentPost.author_id && currentPost.wallet_address !== walletAddress && (
            <button
              onClick={async () => {
                if (!walletAddress) {
                  alert('Please connect your wallet to follow authors');
                  return;
                }
                
                try {
                  // Get user's first author
                  const { data: userAuthors, error: authorsError } = await supabase
                    .from('authors')
                    .select('id')
                    .eq('wallet_address', walletAddress)
                    .limit(1);
                  
                  if (authorsError || !userAuthors || userAuthors.length === 0) {
                    alert('Please create an author profile first');
                    return;
                  }
                  
                  const userAuthorId = userAuthors[0].id;
                  
                  if (isFollowing) {
                    // Unfollow
                    const { error } = await supabase
                      .from('follows')
                      .delete()
                      .eq('follower_entity_type', 'individual')
                      .eq('follower_entity_id', userAuthorId)
                      .eq('following_entity_type', 'individual')
                      .eq('following_entity_id', currentPost.author_id);
                    
                    if (!error) {
                      setIsFollowing(false);
                    }
                  } else {
                    // Follow
                    const followId = `follow_${Date.now()}`;
                    const { error } = await supabase
                      .from('follows')
                      .insert({
                        id: followId,
                        follower_entity_type: 'individual',
                        follower_entity_id: userAuthorId,
                        following_entity_type: 'individual',
                        following_entity_id: currentPost.author_id,
                        created_at: new Date().toISOString()
                      });
                    
                    if (!error) {
                      setIsFollowing(true);
                    }
                  }
                } catch (error) {
                  console.error('Error toggling follow:', error);
                }
              }}
              style={{
                backgroundColor: isFollowing ? t.borderStrong : t.buttonBg,
                color: isFollowing ? t.buttonBg : t.cream,
                padding: '8px 20px',
                borderRadius: '20px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              {isFollowing ? 'Following' : 'Follow'}
            </button>
          )}

          {currentPost.wallet_address === walletAddress && (
            <>
              <button
                onClick={() => editPost(currentPost)}
                className="p-2 hover:bg-stone-100 rounded-full transition-colors"
              >
                <Edit2 size={18} />
              </button>
              <button
                onClick={() => deletePost(currentPost.id)}
                className="p-2 hover:bg-red-50 text-red-600 rounded-full transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Featured Image */}
      {currentPost.featured_image_url && (
        <div style={{ marginBottom: '32px' }}>
          <img
            src={currentPost.featured_image_url}
            alt={currentPost.title}
            style={{
              width: '100%',
              maxHeight: '500px',
              objectFit: 'cover',
              borderRadius: '8px'
            }}
          />
        </div>
      )}

      {/* Render HTML content from WYSIWYG editor */}
      <div
        className="limestone-prose"
        style={{ outline: '4px solid red' }}
        dangerouslySetInnerHTML={{ __html: currentPost.content }}
      />

      <div className="flex items-center gap-6 py-6 border-y border-stone-200">
        <button
          onClick={handleLike}
          disabled={likeLoading}
          className="flex items-center gap-2 px-4 py-2 rounded-full transition-colors"
          style={{
            backgroundColor: hasLiked ? t.buttonBg : 'transparent',
            color: hasLiked ? t.buttonText : t.text,
            border: `2px solid ${t.accent}`,
            cursor: likeLoading ? 'not-allowed' : 'pointer',
            opacity: likeLoading ? 0.6 : 1
          }}
        >
          <Heart 
            size={20} 
            fill={hasLiked ? t.buttonText : 'none'}
            style={{ transition: 'fill 0.2s' }}
          />
          <span>{currentPost.like_count || 0} {hasLiked ? 'Liked' : 'Like'}</span>
        </button>

        <button 
          onClick={onShowCommentModal}
          className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-stone-100 transition-colors"
          style={{
            border: '2px solid transparent'
          }}
        >
          <MessageCircle size={20} />
          <span>{currentPost.comment_count || 0} Comment{currentPost.comment_count !== 1 ? 's' : ''}</span>
        </button>

        <button 
          onClick={onShowShareModal}
          className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-stone-100 transition-colors"
          style={{
            border: '2px solid transparent'
          }}
        >
          <Share2 size={20} />
          <span>Share</span>
        </button>
      </div>
    </div>
  );
}